import { FC, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import { LoadingButton } from "@mui/lab"
import { caseManagement } from "@/gluelayer"

import NavigationBlockPopup from "../../../components/NavigationBlockPopup/NavigationBlockPopup"
import PatientInfoHeader from "../../../containers/LandingLayout/PatientInfoHeader/PatientInfoHeader"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { setAlert } from "../../../core/app/slices/alert/alertSlice"
import {
  forcelockCaseApi,
  lockCaseApi,
  unlockCaseApi,
  uploadCaseZips,
} from "../../../core/app/slices/clinical/clinicalThunkApi"
import { setIsRouteChangeBlocked } from "../../../core/app/slices/navigationPopup/navigationPopupSlice"
import { resetPayload } from "../../../core/app/slices/order/orderSlice"
import { placeManufacturingOrders, submitRetainerFasttrackOrder } from "../../../core/app/slices/order/orderThunkApi"
import { RootState } from "../../../core/app/store"
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt"
import { useMyNavigation } from "../../../hooks/useMyNavigation"
import { UButton, UText } from "../../../ui-component"
import { Box, Grid, Link, Paper } from "../../../ui-component/mui.components"
import OrderSummary from "../Confirmation/OrderSummary"
import ExitOrderAlertPopup from "../ExitOrderAlertPopup"
import DeliveryService from "../Shipping/components/DeliveryService"
import PackageType from "../Shipping/components/PackageType"
import ShippingAddress from "../Shipping/components/ShippingAddress"
import { resetCase } from "../../../core/app/slices/clinical/clinicalSlice"
import { ApplyPromo } from "./ApplyPromo"
import { CASE_STATUS } from "@/modules/Patient/config/status.config"
import OrderCaseLockPopup from "../orderCaseLockPopup"
import { getAWSRUMClient } from "@/core/global/awsRumClient"
import { safeCloseWindow } from "@/core/app/CloseTabContext"
import { setIsCloseTab } from "@/core/app/slices/patients/patientSlice"
import { fetchFormByCaseIdWithMergeData } from "@/core/app/slices/uassist/formThunkApi"
import { FormType } from "@/components/Form/types"
import { RetainerFasttrackOrderType, IRetainerFasttrackOrderPayload } from "@/core/model/interface/AlignerOrder"
import { parseToObject } from "@/core/utils/formatters"
import { BundleType } from "@/core/model/BundleType"

/**
 * record placeorder event
 * @param payload 
 */
const recordPlaceorderEvent = (payload)=>{
  const awsRum = getAWSRUMClient();
  awsRum.addSessionAttributes({
    caseId: payload.caseId,
  })
  awsRum?.recordEvent('clinical_placeorder_event',{
    ...payload
  })
}

const ReviewOrder: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useMyNavigation()
  const { patientId, caseId } = useParams()
  const [showPlaceOrderBtn, setShowPlaceOrderBtn] = useState<boolean>(false)
  const [placingOrder, setPlacingOrder] = useState<boolean>(false)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [isClickBack,setIsClickBack] = useState<boolean>(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const from = searchParams.get("from")
  const { currentOrder,alignerOrderPayload, isAlignerCase, orderSource, retainerFasttrackOrderType } = useAppSelector(
    (state: RootState) => state.orderService,
  )
  
  const { isCloseTab } = useAppSelector((state: RootState) => state.patientService)
  const isRetainerFastrack = searchParams.get("isRetainerFastrack")
  const isRefinement = searchParams.get("isRefinement")
  
  // Build query string with all necessary params
  const buildQueryString = () => {
    const params = new URLSearchParams(searchParams)
    return params.toString() ? `?${params.toString()}` : ''
  }
  
  const queryString = buildQueryString()

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { countryCode } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const { isRouteChangeBlocked } = useAppSelector(
    (state: RootState) => state.navigationPopup,
  )
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isRouteChangeBlocked)

  const LANG_CODE = "en"
  const termsHref = useMemo(() => {
    if (countryCode) {
      return `https://hub.ulabsystems.net/templates/terms/${countryCode.toLowerCase()}_${LANG_CODE}.html`
    }
  }, [countryCode])

  const policyHref = useMemo(() => {
    if (countryCode) {
      return `https://hub.ulabsystems.net/templates/privacy/${countryCode.toLowerCase()}_${LANG_CODE}.html`
    }
  }, [countryCode])

  // State Vars for OrderCaseLockPopup
  const lockPopupSuccessCallback = useRef(null);
  const [isLockPopupOpen, setIsLockPopupOpen] = useState(false)
  const [lockErrorInfo, setLockErrorInfo] = useState<string | null>(null)
  const [lockInfo, setLockInfo] = useState<{
    username: string
    device_info: string
    locked_date: string
  }>({ username: "", device_info: "", locked_date: "" })

  /**
   * Handles the submission of manufacturing orders for both regular aligner orders and retainer fasttrack orders.
   * 
   * @description
   * This function orchestrates the final step of placing a manufacturing order after the user has reviewed
   * and confirmed all order details. It handles two distinct order flows:
   * 
   * 1. **Retainer Fasttrack Flow** (when isRetainerFastrack query param is present):
   *    - Parses the case's udesign_json to extract upper/lower arch types
   *    - Creates a linked uAssist order for retainer manufacturing
   *    - Submits with order_type "Retainer uAssist"
   * 
   * 2. **Regular Manufacturing Order Flow**:
   *    - Places standard manufacturing orders with treatment plan details
   *    - Includes sub-orders if they exist
   *    - Sets order status to 20 (submitted)
   */
  const handlePlaceManufacturingOrder = async () => {
    dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))
    const subOrdersArray = currentOrder.subOrderId
      ? [currentOrder.subOrderId]
      : []

    let res;
    
    // Branch: Retainer Fasttrack Flow
    if(isRetainerFastrack){
      let udesign_json = JSON.parse(caseDetail?.udesign_json)

      const payloadData: IRetainerFasttrackOrderPayload = {
        "case_id": caseId,
        "order_type": retainerFasttrackOrderType,
        "upper_type": udesign_json?.UpperType, // "R" for Retainer, "A" for Aligner
        "lower_type": udesign_json?.LowerType
      }
      if(!retainerFasttrackOrderType){
        const response = await dispatch(
          fetchFormByCaseIdWithMergeData({
            caseId: caseId?.toString(),
            getSavedData: true,
            patientId: patientId?.toString(),
            formType: FormType.UASSIST,
            uassistRound: (caseDetail.uassist_round ?? 0) + (isRefinement ? 1 : 0),
          }),
        )
        if(response?.payload){
          const formData = parseToObject(response.payload?.default_data)
          const type = formData?.bracket_removal?.value.includes("yes") ? RetainerFasttrackOrderType.BRACKET_REMOVAL_UASSIST : RetainerFasttrackOrderType.RETAINER_UASSIST
          payloadData.order_type = type
        } else {
          // Default to Retainer uAssist if we couldn't get the order_type
          payloadData.order_type = RetainerFasttrackOrderType.RETAINER_UASSIST
        }
      }
      if(currentOrder?.subOrderId){
        payloadData.sub_orders = currentOrder.subOrderId
      }
      
      // Add is_refinement flag if present in URL
      if (isRefinement === "true") {
        payloadData.is_refinement = true
      }
      
      res = await dispatch(
        submitRetainerFasttrackOrder({
          orderId: String(currentOrder.orderId),
          payload: payloadData
        })
      )
    }
    // Branch: Regular Manufacturing Order Flow
    else{
      res = await dispatch(
        placeManufacturingOrders({
          orderId: currentOrder.orderId + "",
          payload: {
            txplan_name: caseDetail.txplanName || "",
            txplan_subname: caseDetail.txplanSubname || "",
            sub_orders: subOrdersArray,
            status: 20,
          },
        }),
      )
    }
    
    recordPlaceorderEvent({
      patientId,
      caseId,
      order:currentOrder,
      alignerOrderPayload,
      isSuccessful: res?.payload?.status === 200,
    })
    setPlacingOrder(false)
    if (res?.payload?.status === 200) {
      dispatch(resetPayload())
      dispatch(unlockCaseApi({ patientId, caseId }))
      navigate(
        `/order/patient/${patientId}/case/${caseId}/order/${currentOrder.orderId}/confirmation${queryString}`,
      )
      
      caseManagement.closeCase();
      dispatch(resetCase())
    } 
    else {
      dispatch(
        setAlert({
          message: t("overview.order.orderfailed"),
          isError: true,
        }),
      )
    }
  }

  // when you click next ,please upload zips
  // sync zips to backend server
  const uploadzipsAndPlaceOrder = () => {
    caseManagement.getCaseFiles(["bulk2","bulk10"]).then((r) => {
      const zips = []
      if (r) {
        for (const k in r) {
          zips.push({
            fileName: k,
            file: r[k],
          })
        }
        dispatch(
          uploadCaseZips({
            zips,
            patientId,
            caseId,
            skipLockCheck:"0" 
          }),
        ).then((res) => {
          handlePlaceManufacturingOrder()
        })
      }
    })
  }


  useEffect(() => {
    // On Init, lock the case
    lockCase()
  }, []);

  useEffect(() => {
    if (placingOrder) {
      lockCase(uploadzipsAndPlaceOrder)
    }
  }, [placingOrder])

  useEffect(() => {
    if (
      currentOrder.orderId &&
      caseDetail.caseDisposition !== CASE_STATUS.ORDER_SUBMITTED
    ) {
      dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
    }
  }, [dispatch, caseDetail, currentOrder])

  const lockCase = (nextFunc=null)=>{
    dispatch(lockCaseApi({ patientId, caseId })).then(({payload})=>{
      if(String(payload.status).toLowerCase()!=="success"){

        const { errors, username, device_info, locked_date } = payload.data
        const { error_message } = errors[0]
        const info = error_message.split("::")[0]
        
        setLockErrorInfo(info)
        setLockInfo({
          username,
          device_info,
          locked_date
        })
        if(nextFunc){
          lockPopupSuccessCallback.current = nextFunc
        }
        setIsLockPopupOpen(true)
      }else{
        nextFunc && nextFunc()
      }

    })
  }

  const forceUnlock = ()=>{
    dispatch(forcelockCaseApi({ patientId, caseId }))
      .then(({ payload }) => {
        const { status } = payload
        if (status === "Success") {
          setPlacingOrder(false)
          setIsLockPopupOpen(false)
          lockPopupSuccessCallback.current && lockPopupSuccessCallback.current()
          lockPopupSuccessCallback.current = null
        }else{
          // unable to lock scenario
        }
      }
    )
  }

  const getAlignerCaseCost = () => {
    let alignerCaseCost = "";

    if (currentOrder?.bundle?.type === BundleType.ALIGNER || currentOrder?.bundle?.type === BundleType.RETAINER) {
      alignerCaseCost = currentOrder.bundle?.alignercase?.cost
        ? `$${currentOrder.bundle.alignercase.cost}`
        : "Included";
    } else if (currentOrder?.alacarte?.pricing_option) {
      alignerCaseCost = currentOrder.alacarte?.alignercase?.cost
        ? `$${currentOrder.alacarte.alignercase.cost}`
        : "Included";
    } else {
      alignerCaseCost = "";
    }
    return alignerCaseCost;
  }

  return (
    <>
      <Grid
        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
      >
        <Grid item minWidth={{ lg: "1152px" }} md={11} sm={10}>
          <PatientInfoHeader
            chipLabel={"Place Order"}
            cancelBtnText={t("button.close")}
            onBtnCancelClick={() => {
              dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))
              setShowAlert(true)
            }}
            chipSx={{
              background: "#0288D1",
              fontSize: "13px",
              fontWeight: "400px",
              color: "primary.contrastText",
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <UText component={"div"} variant="h4">
              {t("overview.order.review")}
            </UText>
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        direction={"row"}
        gap={"24px"}
        sx={{
          display: "flex",
          pt: 6,
          justifyContent: "center",
        }}
      >
        <Grid item lg={7} md={7} sm={10}>
          <ShippingAddress isReviewOrder={true} />

          <Box sx={{ mt: 2 }}>
            <DeliveryService isReviewOrder={true} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <PackageType isReviewOrder={true} />
          </Box>
          {currentOrder?.orderId && !!isAlignerCase ? (
            <Paper
              elevation={0}
              sx={{
                py: 5,
                px: 2,
                mt: 3,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <UText variant="body1" sxProp={{ px: 2, py: "2px" }}>
                  {"Additional aligner storage case"}
                </UText>
              </Box>
              <Box
                sx={{
                  px: "12px",
                }}
              >
                <UText sxProp={{ p: "12px" }} variant={"subtitle2"}>
                  {getAlignerCaseCost()}
                </UText>
              </Box>
            </Paper>
          ) : null}
        </Grid>
        <Grid
          item
          justifyContent={"centre"}
          lg={4}
          md={4}
          sm={10}
          minWidth={{ lg: "446px" }}
        >
          <Paper elevation={0} sx={{ p: 2 }}>
            <OrderSummary
              isreview={true}
              setShowPlaceOrderBtn={setShowPlaceOrderBtn}
            />
            <ApplyPromo />
            <LoadingButton
              loading={placingOrder}
              loadingPosition="start"
              variant="contained"
              disabled={showPlaceOrderBtn}
              size="large"
              onClick={() => setPlacingOrder(true)}
              sx={{
                width: "100%",
                mt: 4,
                mb: 2,
                ".MuiLoadingButton-loadingIndicator": {
                  left: { lg: "30%", md: "20%" },
                },
              }}
            >
              {placingOrder
                ? isRetainerFastrack ? "Submitting to uAssist and Placing Order" : t("overview.order.placingorder")
                : isRetainerFastrack ? "Submit to uAssist and Place Order" : t("overview.order.placeorder")}
            </LoadingButton>
            <Box sx={{ mb: 4 }}>
              <UText component={"div"} variant={"caption"}>
                {t("overview.order.tnc.0")}
                <Link href={termsHref} target={"_blank"}>
                  {t("overview.order.tnc.1")}
                </Link>
                {t("overview.order.tnc.2")}
                <Link href={policyHref} target={"_blank"}>
                  {t("overview.order.tnc.3")}
                </Link>
              </UText>
            </Box>
          </Paper>
        </Grid>
        <Grid item minWidth={{ lg: "1152px" }} md={11} sm={10}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              pb: 6,
              pt: 3,
            }}
          >
            <UButton
              variant="outlined"
              onClickHandler={() => {
                dispatch(
                  setIsRouteChangeBlocked({ isRouteChangeBlocked: false }),
                )
                setIsClickBack(true);
                navigate(`/order/patient/${patientId}/case/${caseId}/shipping${queryString}`)
              }}
              sxProp={{ width: "96px", height: "36px" }}
              startIcon={
                <ChevronLeftIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
              }
              btnText={t("button.back")}
            />
          </Box>
        </Grid>
      </Grid>
      <OrderCaseLockPopup 
        isPopupOpen={isLockPopupOpen}
        setIsPopupOpen={setIsLockPopupOpen}
        lockInfo={lockInfo}
        errorInfo={lockErrorInfo}
        forceUnlock={forceUnlock}
      />
      <ExitOrderAlertPopup
        showAlert={showAlert}
        closeAlert={() => {
          dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: true }))
          setShowAlert(false)
        }}
        buttonTxt={t("button.cancel")}
      />
      <NavigationBlockPopup
        cancelNavigation={()=>{
          if(isCloseTab) {
            dispatch(setIsCloseTab(false))
            return
          }
          cancelNavigation&&(cancelNavigation as ()=>void)()
          dispatch(
            setIsRouteChangeBlocked({ isRouteChangeBlocked: true }),
          )
          setIsClickBack(false)
        }}
        confirmNavigation={()=>{
          if(isCloseTab) {
            safeCloseWindow()
            return
          }
          if(!isClickBack){
            caseManagement.closeCase();
            dispatch(resetCase())
            setIsClickBack(false)
          }
          confirmNavigation&&(confirmNavigation as ()=>void)()
        }}
        showPrompt={showPrompt || isCloseTab}
      />
    </>
  )
}

export default ReviewOrder
